import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGuide } from '../../hooks/useGuide';

const CATEGORY_ORDER = ['federal', 'state', 'local', 'judges', 'measures'];

const CATEGORY_LABELS = {
  federal: 'Federal',
  state: 'State',
  local: 'County & Local',
  judges: 'Judges',
  measures: 'Ballot Measures',
};

const JUDGES_EXPLAINER =
  'A retention vote asks: should this judge keep their seat? A "Yes" vote keeps them in office; a "No" vote removes them.';

function ContestItem({ contest, selected, onSelect }) {
  const isMeasure = !!contest.measure;

  return (
    <div
      style={{
        borderBottom: '1px solid #1a1a1a',
        paddingBottom: 16,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 20,
            height: 20,
            border: `2px solid ${selected ? 'var(--g-orange)' : '#444'}`,
            background: selected ? 'var(--g-orange)' : 'transparent',
            borderRadius: 2,
            flexShrink: 0,
            marginTop: 2,
            transition: 'background 0.1s, border-color 0.1s',
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 17 }}>
            {contest.officeName ?? contest.measure?.title ?? 'Unknown Race'}
          </div>
          {contest.description && (
            <div style={{ fontSize: 13, color: 'var(--g-muted)', marginTop: 2 }}>
              {contest.description}
            </div>
          )}
          {isMeasure && contest.measure?.summary && (
            <div
              style={{
                fontSize: 13,
                color: '#bbb',
                marginTop: 6,
                padding: '8px 12px',
                background: '#0d0d0d',
                borderLeft: '2px solid var(--g-orange)',
              }}
            >
              {contest.measure.summary}
              {contest.measure.fiscalImpact && (
                <div style={{ marginTop: 4, color: 'var(--g-muted)' }}>
                  💰 {contest.measure.fiscalImpact}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isMeasure ? (
        <div style={{ display: 'flex', gap: 8, marginLeft: 30 }}>
          {['for', 'against'].map((opt) => (
            <button
              key={opt}
              onClick={() => onSelect(contest.id, selected === opt ? null : opt)}
              style={{
                flex: 1,
                padding: '10px',
                background: selected === opt ? 'var(--g-orange)' : '#111',
                color: selected === opt ? 'var(--g-black)' : 'var(--g-white)',
                border: `1px solid ${selected === opt ? 'var(--g-orange)' : '#333'}`,
                borderRadius: 'var(--g-radius)',
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              {opt === 'for' ? 'For' : 'Against'}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 30 }}>
          {contest.candidates.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelect(contest.id, selected === c.id ? null : c.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: selected === c.id ? '#1a0d00' : '#111',
                border: `1px solid ${selected === c.id ? 'var(--g-orange)' : '#333'}`,
                borderRadius: 'var(--g-radius)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: `2px solid ${selected === c.id ? 'var(--g-orange)' : '#555'}`,
                  background: selected === c.id ? 'var(--g-orange)' : 'transparent',
                  flexShrink: 0,
                  transition: 'background 0.1s',
                }}
              />
              <div>
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontWeight: 700,
                    fontSize: 16,
                    color: selected === c.id ? 'var(--g-white)' : '#ddd',
                  }}
                >
                  {c.name}
                </span>
                {c.party && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 12,
                      color: 'var(--g-muted)',
                      fontFamily: 'Barlow Condensed, sans-serif',
                    }}
                  >
                    {c.party}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GuideBallotChecklist() {
  const navigate = useNavigate();
  const { lookupResult, selections, selectCandidate, clearSelection, selectedCount, totalCount } =
    useGuide();

  useEffect(() => {
    if (!lookupResult) navigate('/', { replace: true });
  }, [lookupResult, navigate]);

  if (!lookupResult) return null;

  const { ballot } = lookupResult;
  const pct = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0;

  function handleSelect(contestId, value) {
    if (value === null) {
      clearSelection(contestId);
    } else {
      selectCandidate(contestId, value);
    }
  }

  return (
    <div>
      <p className="g-label">Step 3 — Make Your Picks</p>
      <h1 className="g-h1" style={{ marginBottom: 16 }}>
        Your ballot.
      </h1>

      <div className="g-progress-wrap">
        <div className="g-progress-label">
          {selectedCount} of {totalCount} decided
        </div>
        <div className="g-progress-bar">
          <div className="g-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {CATEGORY_ORDER.map((cat) => {
        const contests = ballot.contests?.[cat] ?? [];
        if (contests.length === 0) return null;
        const isJudges = cat === 'judges';
        return (
          <details key={cat} open={cat === 'federal'} style={{ marginBottom: 8 }}>
            <summary
              style={{
                listStyle: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                background: '#111',
                border: '1px solid #222',
                borderRadius: 'var(--g-radius)',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 17,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              <span>{CATEGORY_LABELS[cat]}</span>
              <span style={{ color: 'var(--g-muted)', fontWeight: 600, fontSize: 14 }}>
                {contests.length} race{contests.length !== 1 ? 's' : ''}
              </span>
            </summary>
            <div style={{ padding: '16px 0 0' }}>
              {isJudges && (
                <div
                  style={{
                    fontSize: 13,
                    color: '#aaa',
                    background: '#0d0d0d',
                    borderLeft: '2px solid #444',
                    padding: '8px 12px',
                    marginBottom: 16,
                  }}
                >
                  {JUDGES_EXPLAINER}
                </div>
              )}
              {contests.map((contest) => (
                <ContestItem
                  key={contest.id}
                  contest={contest}
                  selected={selections[contest.id]}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </details>
        );
      })}

      <div style={{ marginTop: 24 }}>
        <button className="g-btn-primary" onClick={() => navigate('/guide/save-and-share')}>
          {selectedCount === totalCount
            ? 'Done — see my cheat sheet →'
            : `Continue with ${selectedCount} picks →`}
        </button>
      </div>
    </div>
  );
}
